<script setup lang="ts">
import { computed, type HTMLAttributes, ref } from 'vue';
import type { PrimitiveProps } from 'radix-vue';
import { useI18n } from 'vue-i18n';
import LanguageSelectorLanguageButton from '@/components/LanguageSelectorLanguageButton.vue';
import { Button, Icon } from '@/components/ui';
import { useGlobal } from '@/composables';
import type { LanguageCode, LanguageSelectorLocaleData } from '@/types';

interface Props extends PrimitiveProps {
  triggerClass?: HTMLAttributes['class'];
}

defineProps<Props>();

const dialog = ref<HTMLDialogElement | null>(null);

const open = () => {
  dialog.value?.showModal();
};

const close = () => {
  dialog.value?.close();
};

const { t } = useI18n();
const { language } = useGlobal();

const selectLanguage = (_language: LanguageSelectorLocaleData) => {
  language.value = _language.language;
  close();
};

type Regions = ['global', 'asia-pacific', 'north-america', 'latin-america', 'europe'];

const languages = ref<
  {
    region: Regions[number];
    country?: string;
    language: LanguageCode;
  }[]
>([
  { region: 'global', language: 'en' },
  { region: 'asia-pacific', language: 'en-AS' },
  { region: 'north-america', language: 'en-US' },
  { region: 'latin-america', language: 'es-BR' },
  { region: 'latin-america', language: 'pt-BR' },
  { region: 'europe', country: 'france', language: 'fr-FR' },
  { region: 'europe', country: 'portugal', language: 'pt-PT' },
  { region: 'europe', country: 'spain', language: 'es-ES' },
  { region: 'europe', country: 'germany', language: 'de-DE' },
  { region: 'europe', country: 'benelux', language: 'nl-BX' },
  { region: 'europe', country: 'benelux', language: 'fr-BX' },
  { region: 'europe', country: 'united-kingdom', language: 'en-GB' },
  { region: 'europe', country: 'poland', language: 'pl-PL' },
  { region: 'europe', country: 'nordics', language: 'en-ND' },
  { region: 'europe', country: 'czech', language: 'cs-CZ' },
]);

const nonEuropeanRegions = computed(
  () => new Set(languages.value.filter((l) => l.region !== 'europe').map((l) => l.region)),
);
const europeanCountries = computed(
  () =>
    new Set(languages.value.filter((l) => l.region === 'europe').map((l) => l.country as string)),
);

const languagesPerRegion = computed(
  () => (region: Regions[number]) => languages.value.filter((l) => l.region === region),
);
const languagesPerCountry = computed(
  () => (country: string) => languages.value.filter((l) => l.country === country),
);

const currentLanguage = computed(
  () => languages.value.find((l) => l.language === language.value) ?? languages.value[0],
);
</script>

<template>
  <dialog
    class="custom-scrollbar h-full w-[calc(100%_-_1rem)] max-w-[1200px] flex-col rounded-md bg-white p-10 text-left text-shuttle-gray-500 backdrop:bg-black/60 md:p-[60px]"
    ref="dialog"
  >
    <div class="flex size-full flex-col gap-10 md:gap-[60px]">
      <Button
        class="absolute right-4 top-4 text-shuttle-gray-500 hover:bg-transparent hover:text-primary focus-visible:bg-transparent focus-visible:text-primary"
        variant="icon"
        @click="close"
      >
        <Icon class="size-6" name="close" />
      </Button>
      <div class="space-y-4">
        <h2 class="text-xl font-medium tracking-wide md:text-5xl">
          {{ t('language-selector-dialog.title') }}
        </h2>
      </div>
      <div class="space-y-10 md:hidden">
        <div class="divide-y divide-shuttle-gray-600/10">
          <div
            class="flex flex-wrap items-center gap-3 py-4"
            v-for="region in nonEuropeanRegions"
            :key="region"
          >
            <span class="block border-r border-shuttle-gray-600/10 pr-3 text-sm font-bold">{{
              t(`language-selector-dialog.regions.${region}`)
            }}</span>
            <LanguageSelectorLanguageButton
              :with-flags="false"
              @click="selectLanguage(language)"
              v-for="language in languagesPerRegion(region)"
              :key="language.language"
              :language="language.language"
            />
          </div>
        </div>
        <div class="divide-y divide-shuttle-gray-600/10">
          <h3 class="mb-5 text-2xl font-light">
            {{ t(`language-selector-dialog.regions.europe`) }}
          </h3>
          <div
            class="flex flex-wrap items-center gap-3 py-4"
            v-for="country in europeanCountries"
            :key="country"
          >
            <span class="block border-r border-shuttle-gray-600/10 pr-3 text-sm font-bold">{{
              t(`language-selector-dialog.countries.${country}`)
            }}</span>
            <LanguageSelectorLanguageButton
              :with-flags="false"
              @click="selectLanguage(language)"
              v-for="language in languagesPerCountry(country)"
              :key="language.language"
              :language="language.language"
            />
          </div>
        </div>
      </div>
      <div class="hidden grid-cols-7 gap-4 md:grid">
        <div class="col-span-2 grid gap-10">
          <div class="flex flex-col gap-5" v-for="region in nonEuropeanRegions" :key="region">
            <span class="block text-2xl font-light">{{
              t(`language-selector-dialog.regions.${region}`)
            }}</span>
            <ul class="space-y-1">
              <li v-for="language in languagesPerRegion(region)" :key="language.language">
                <LanguageSelectorLanguageButton
                  @click="selectLanguage(language)"
                  :language="language.language"
                />
              </li>
            </ul>
          </div>
        </div>
        <div class="flex justify-start">
          <div class="ml-4 h-full w-px bg-shuttle-gray-600/10" />
        </div>
        <div class="col-span-4">
          <h3 class="mb-2.5 text-2xl font-light">
            {{ t(`language-selector-dialog.regions.europe`) }}
          </h3>
          <div class="grid h-full grid-cols-3 gap-4">
            <div v-for="country in europeanCountries" :key="country">
              <span class="mb-2 block text-lg font-medium">{{
                t(`language-selector-dialog.countries.${country}`)
              }}</span>
              <ul class="space-y-1">
                <li v-for="language in languagesPerCountry(country)" :key="language.language">
                  <LanguageSelectorLanguageButton
                    @click="selectLanguage(language)"
                    :language="language.language"
                  />
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </dialog>
  <slot :open="open">
    <Button class="gap-2" :class="triggerClass" @click="open">
      <span>
        {{
          currentLanguage.country
            ? t(`language-selector-dialog.countries.${currentLanguage.country}`)
            : t(`language-selector-dialog.regions.${currentLanguage.region}`)
        }}
      </span>
      <div class="h-[1em] w-px bg-current" />
      <span class="uppercase">{{ currentLanguage.language.split('-')[0] }}</span>
      <Icon name="chevron-right" class="size-3 rotate-90" />
    </Button>
  </slot>
</template>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  @apply w-[0px] bg-transparent;
}
</style>
